import axios from 'axios';

/**
 * Revenue Target Service
 *
 * Wraps the `/revenue-targets` family of endpoints introduced in the
 * 2026-05-12 Phase 3 release. Pairs with the forecast endpoint exposed
 * under `/dashboard/stats/forecast` (see `dashboardStatsService`).
 *
 * All calls accept an optional AbortSignal so callers (the store) can
 * cancel an in-flight request when a higher-priority refresh fires
 * before the response comes back. Cancellation surfaces as
 * `error.code === 'ERR_CANCELED'` — consumers should ignore that.
 *
 * Note on shapes:
 *   - The "single year" endpoints return the row itself (no envelope).
 *   - The "list" endpoint returns `{ targets: [...] }`.
 *   - The benchmarks endpoint returns `{ benchmarks: [...] }`.
 *   - The monthly preview endpoint returns
 *     `{ year, annual_target_excl_vat, months, resolution }`.
 *
 * 404 on `getByYear` is *not* an error — it means "no target set yet".
 * Callers should catch and treat as an empty state.
 */
export const revenueTargetService = {
  /**
   * Curated seasonal benchmarks (zonwering, rolluiken, ...). The list
   * is static per release, so it's safe to cache for the session — see
   * `revenueTargetStore.fetchBenchmarks()` which dedupes calls.
   */
  async getBenchmarks({ signal } = {}) {
    const response = await axios.get('/revenue-targets/benchmarks', { signal });
    return response.data?.benchmarks || [];
  },

  /**
   * Single year. Backend returns 404 when no target exists yet — we
   * translate that to `null` so the caller doesn't need a try/catch
   * just to detect "empty".
   */
  async getByYear(year, { signal } = {}) {
    try {
      const response = await axios.get(`/revenue-targets/${year}`, { signal });
      return response.data;
    } catch (error) {
      if (error?.response?.status === 404) return null;
      throw error;
    }
  },

  /**
   * Year range — used by the year-switcher in the dashboard widget to
   * know which years already have a configured target.
   */
  async list({ fromYear, toYear } = {}, { signal } = {}) {
    const params = {};
    if (fromYear != null) params.from_year = fromYear;
    if (toYear != null) params.to_year = toYear;
    const response = await axios.get('/revenue-targets', { params, signal });
    return response.data?.targets || [];
  },

  /**
   * Full upsert — replaces the row for `year`. The backend accepts
   * partial bodies (`PATCH`-semantics) on this endpoint too, but we
   * use `PUT` to match the documented spec.
   */
  async save(year, body, { signal } = {}) {
    const response = await axios.put(`/revenue-targets/${year}`, body, { signal });
    return response.data;
  },

  /**
   * Partial update. Identical to `save` for our purposes but uses
   * `PATCH` so the spec-following client semantics match (some
   * future endpoints might enforce strict partial vs. replace).
   */
  async patch(year, body, { signal } = {}) {
    const response = await axios.patch(`/revenue-targets/${year}`, body, { signal });
    return response.data;
  },

  async remove(year, { signal } = {}) {
    await axios.delete(`/revenue-targets/${year}`, { signal });
  },

  /**
   * Resolved monthly distribution for live-preview in the settings
   * form. Returns the same `{ months, resolution }` shape the backend
   * uses internally for the dashboard forecast. Cheap enough to
   * recompute on every strategy / benchmark change.
   */
  async getMonthly(year, { signal } = {}) {
    const response = await axios.get(`/revenue-targets/${year}/monthly`, { signal });
    return response.data;
  },
};

export default revenueTargetService;
